import {
	type ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Checkbox,
	Chip,
	FormControlLabel,
	Grid,
	Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import _ from 'lodash';

import {
	Button,
	Dialog,
	Table,
	type TableAction,
	TextField,
} from 'src/components';
import {
	ICourse,
	type IInstitution,
	type IInstitutionCourse,
} from 'src/models';
import useStyles from './CourseAssignment-styles';

import { type RootState } from 'src/store';
import { useDispatch, useSelector } from 'react-redux';
import courseActions from 'src/store/Courses/actions';
import institutionActions from 'src/store/Institutions/actions';

const columns = [
	{
		key: 'name',
		label: 'Nombre',
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[22:8]
		render: (value: any, row: any) => value,
	},
];

interface Props {
	open: boolean;
	handleOpen: () => void;
	institution: IInstitution;
}

function CourseAssignmentDialog({ handleOpen, institution, open }: Props) {
	const classes = useStyles();
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();
	const { courses, institutionCourses } = useSelector(
		(state: RootState) => state.courses,
	);

	const getCourses = () => dispatch(courseActions.getAll());
	const getInstitutionCourses = (institutionId: number) =>
		dispatch(courseActions.getInstitutionCourses(institutionId));
	const cleanInstitutionCourses = () =>
		dispatch(courseActions.setInstitutionCourses([]));
	const assignCourses = (
		institutionId: number,
		institutionCourses: IInstitutionCourse[],
	) =>
		dispatch(
			institutionActions.assignCourses(institutionId, institutionCourses),
		);

	// NOTE:
	// ./src/modules/Private/Courses/Course.tsx[22:8]
	const [coursesToAssign, setCoursesToAssign] = useState<any[]>([]);

	useEffect(() => {
		if (open) {
			getCourses();
		} else {
			cleanInstitutionCourses();
		}
	}, [open]);

	useEffect(() => {
		if (open) getInstitutionCourses(institution.id || 0);
	}, [institution.id]);

	useEffect(() => {
		setCoursesToAssign(institutionCourses);
	}, [institutionCourses]);

	function handleAssign() {
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[22:8]
		const institutionCourses = coursesToAssign.map((course: any) => ({
			active: true,
			courseId: course.id,
			institutionId: institution.id,
			showInstitution: Boolean(course.showInstitution),
			showPosition: Boolean(course.showPosition),
		}));

		assignCourses(institution.id || 0, institutionCourses);
		handleOpen();
	}

	const handleConfig = useCallback(
		(
			courseId: number,
			property: 'showInstitution' | 'showPosition',
			value: boolean,
		) => {
			const index = coursesToAssign.findIndex(
				(course) => course.id === courseId,
			);
			const _coursesToAssign = [...coursesToAssign];
			_coursesToAssign[index][property] = value;
			setCoursesToAssign(_coursesToAssign);
		},
		[coursesToAssign],
	);

	const actions: TableAction[] = useMemo(
		() => [
			{
				label: '',
				onClick: (row: any) => {},
				render: function Component(row: any) {
					return (
						<FormControlLabel
							control={
								<Checkbox
									checked={Boolean(row.showInstitution)}
									color={'secondary'}
									onChange={(
										event: ChangeEvent<HTMLInputElement>,
									) => {
										handleConfig(
											row.id,
											'showInstitution',
											event.target.checked,
										);
									}}
								/>
							}
							label={'Institución'}
						/>
					);
				},
			},
			{
				label: '',
				onClick: (row: any) => {},
				render: function Component(row: any) {
					return (
						<FormControlLabel
							control={
								<Checkbox
									checked={Boolean(row.showPosition)}
									color={'secondary'}
									onChange={(
										event: ChangeEvent<HTMLInputElement>,
									) => {
										handleConfig(
											row.id,
											'showPosition',
											event.target.checked,
										);
									}}
								/>
							}
							label={'Puesto'}
						/>
					);
				},
			},
		],
		[coursesToAssign],
	);

	return (
		<>
			<Dialog
				content={
					<Grid container spacing={2}>
						<Grid item style={{ marginBottom: 32 }} xs={12}>
							<Typography
								className={classes.title}
								style={{ marginBottom: 24 }}
							>
								{' '}
								Cursos disponibles{' '}
							</Typography>

							<Autocomplete
								autoHighlight
								getOptionLabel={(option: any) => option.name}
								multiple
								noOptionsText={'No hay opciones'}
								onChange={(
									e: ChangeEvent<any>,
									values: any,
								) => {
									setCoursesToAssign(
										_.uniqBy(
											values,
											(value: any) => value.id,
										),
									);
								}}
								options={courses}
								renderInput={(params) => (
									<TextField
										{...params}
										variant={'standard'}
									/>
								)}
								renderTags={(value: any[], getTagProps) =>
									value.map((option: any, index: number) => (
										<Chip
											key={index}
											label={option.name}
											variant={'outlined'}
											{...getTagProps({ index })}
										/>
									))
								}
								value={coursesToAssign}
							/>
						</Grid>

						<Grid item xs={12}>
							<Typography className={classes.title}>
								{' '}
								Cursos asignados{' '}
							</Typography>

							<Table
								actions={actions}
								actionsLabel={'Constancia (Para usuario)'}
								columns={columns}
								data={coursesToAssign}
								hidePagination
							/>
						</Grid>

						<Grid className={classes.buttonContainer} item xs={12}>
							<Button
								backgroundColor={'white'}
								onClick={handleOpen}
							>
								Cancelar
							</Button>

							<Button actionType={'save'} onClick={handleAssign}>
								Guardar cambios
							</Button>
						</Grid>
					</Grid>
				}
				fullScreen={window.innerWidth <= 960}
				handleOpen={handleOpen}
				maxWidth={'md'}
				open={open}
				title={`Cursos - ${institution.name || ''}`}
			/>
		</>
	);
}

export default CourseAssignmentDialog;
